import { defer, first, repeat, takeWhile } from "rxjs";
import { WalletLoadedEvent, WalletLockedEvent, WalletUpdatedEvent } from "./models/blade";
import { noExtensionError, noSessionError } from "./models/errors";
/**
 * Implements Hedera Signer interface.
 * https://hips.hedera.com/hip/hip-338
 *
 * Proxies Extension wallet functions to decouple dApp code from
 * Blade's implementation of the wallet.
 */
export class BladeSigner {
    constructor() {
        /** @hidden */
        this._activeWallet = null;
        document.addEventListener(WalletLoadedEvent, async () => {
            this._activeWallet = (await this._getBladeExtension()).getActiveWallet();
        });
        document.addEventListener(WalletUpdatedEvent, async () => {
            var _a;
            (_a = this._onAccountChanged) === null || _a === void 0 ? void 0 : _a.call(this);
        });
        document.addEventListener(WalletLockedEvent, async () => {
            var _a;
            (_a = this._onWalletLocked) === null || _a === void 0 ? void 0 : _a.call(this);
        });
    }
    /** @hidden */
    _pollInterface() {
        return defer(() => new Promise((resolve) => resolve(window.bladeConnect))).pipe(repeat({ count: 50, delay: 200 }), takeWhile(value => !value, true), first(value => !!value, undefined));
    }
    /** @hidden */
    async _getBladeExtension() {
        const extensionInterface = window.bladeConnect;
        if (extensionInterface) {
            return extensionInterface;
        }
        return new Promise((resolve, reject) => {
            this._pollInterface().subscribe(extInterface => {
                if (!extInterface) {
                    // use of method on BladeSigner before using createSession
                    reject(noExtensionError());
                    return;
                }
                resolve(extInterface);
            });
        });
    }
    /**
     * @param callback synchronous function to run at every account change
     */
    onAccountChanged(callback) {
        this._onAccountChanged = callback;
    }
    /**
     * @param callback synchronous function to run every time wallet is locked
     */
    onWalletLocked(callback) {
        this._onWalletLocked = callback;
    }
    /**
     * @returns Network map currently in use by Blade Wallet.
     */
    getNetwork() {
        var _a;
        return ((_a = this._activeWallet) === null || _a === void 0 ? void 0 : _a.getNetwork()) || {};
    }
    /**
     * @returns Array of Hedera mirror network nodes.
     */
    getMirrorNetwork() {
        var _a;
        return ((_a = this._activeWallet) === null || _a === void 0 ? void 0 : _a.getMirrorNetwork()) || [];
    }
    /**
     * Create a new session with the Blade Wallet Extension.
     *
     * All calls will fail until the promise resolves.
     *
     * @returns Promise that resolves when a new session to the Blade Wallet
     * has succeeded.
     */
    async createSession(params) {
        // store the blade extension here
        // the logic is that some methods on the Signer interface are sync
        this._activeWallet = await (await this._getBladeExtension()).createSession(params === null || params === void 0 ? void 0 : params.network, params === null || params === void 0 ? void 0 : params.dAppCode);
    }
    /**
     * Kills a session with the Blade Wallet Extension.
     */
    async killSession() {
        return (await this._getBladeExtension()).killSession();
    }
    /**
     * @hidden
     */
    async _getActiveWallet() {
        const wallet = (await this._getBladeExtension()).getActiveWallet();
        this._activeWallet = wallet;
        if (wallet == null) {
            throw noSessionError();
        }
        return this._activeWallet;
    }
    /**
     * @param messages Array of messages to sign.
     * @returns Promise that resolves to array of Signed messages.
     */
    async sign(messages) {
        return (await this._getActiveWallet()).sign(messages);
    }
    /**
     * Sign a hedera transaction with Blade Wallet.
     * @param transaction A Hedera transaction.
     * @returns A promise that resolves to the transaction with the signature appended.
     */
    async signTransaction(transaction) {
        return (await this._getActiveWallet()).signTransaction(transaction);
    }
    async call(request) {
        return (await this._getActiveWallet()).call(request);
    }
    /**
     * Determines if all the properties required are set and sets the transaction ID.
     *
     * If the transaction ID was already set it checks if the account ID of it is
     * the same as the users.
     *
     * @param transaction
     * @returns Promise that resolves to Transaction with correct transaction ID.
     */
    async checkTransaction(transaction) {
        return (await this._getActiveWallet()).checkTransaction(transaction);
    }
    /**
     * Sets the transaction ID of the transaction to the current account ID of the signer.
     * @param transaction
     */
    async populateTransaction(transaction) {
        return (await this._getActiveWallet()).populateTransaction(transaction);
    }
    /**
     * @returns LedgerId of network currently being used by Blade Wallet.
     */
    getLedgerId() {
        var _a;
        return ((_a = this._activeWallet) === null || _a === void 0 ? void 0 : _a.getLedgerId()) || null;
    }
    /**
     * @returns AccountId of wallet of active account.
     */
    getAccountId() {
        if (!this._activeWallet) {
            throw noSessionError();
        }
        return this._activeWallet.getAccountId();
    }
    /**
     * @returns Promise that resolves to account balance of active account.
     */
    async getAccountBalance() {
        return (await this._getActiveWallet()).getAccountBalance();
    }
    /**
     * @returns Promise that resolves to AccountInfo of the account currently active.
     */
    async getAccountInfo() {
        return (await this._getActiveWallet()).getAccountInfo();
    }
    /**
     * @returns Promise that resolves to array of records of recent account transactions.
     */
    async getAccountRecords() {
        return (await this._getActiveWallet()).getAccountRecords();
    }
}
